import { Clock3, Info, Sparkles } from 'lucide-react'

import { FormattedDateTime } from '@/frontend/components/formatted-datetime'
import { Badge } from '@/frontend/components/ui/badge'
import { ExpandableContent } from '@/frontend/components/ui/expandable-content'
import { RichTextContent } from '@/frontend/components/ui/rich-text-content'
import { useGetPatientSummary } from '@/frontend/hooks/api/patients/use-get-patient-summary'

/** The displayed generation date uses the clinical request time to indicate the summary's source freshness. */
export function PatientSummarySection({ patientId }: { patientId: string }) {
  const { data: summary } = useGetPatientSummary(patientId)
  if (!summary?.content) return null

  return (
    <section className="min-w-0 rounded-lg border border-ai-border bg-card p-0.5">
      <div className="flex flex-wrap items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-muted-foreground">
        <Sparkles className="size-3.5 text-ai-fg" />
        <h2>AI Summary</h2>
        <Badge variant="neutral" size="xs">
          Experimental
        </Badge>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {summary.stale && (
            <Badge variant="pending" size="xs">
              <Clock3 aria-hidden="true" />
              Stale
            </Badge>
          )}
          <span className="text-2xs font-normal">
            Generated <FormattedDateTime value={summary.requestedAt} clinicId={null} />
          </span>
        </div>
      </div>
      <div className="px-2.5 pt-0.5">
        <ExpandableContent
          maxHeight={100}
          footer={
            <p className="flex items-start justify-end gap-1 text-right text-xs text-warning-accent">
              <Info className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
              <span className="font-medium">
                AI can invent, exaggerate, or miss important details. Always check the patient
                record before acting.
              </span>
            </p>
          }
        >
          <RichTextContent html={summary.content} className="select-text break-words" />
        </ExpandableContent>
      </div>
    </section>
  )
}
